import React, { useEffect, useState, useRef } from 'react';
import { API_CONFIG, getApiUrl, getAuthHeaders } from './config/api';
import { formatDate } from './utils/dateFormat';

interface PatientPhoto {
  id: number;
  fileName: string;
  originalFileName?: string;
  filePath: string;
  dateTaken?: string;
  category?: string;
  contentType?: string;
  fileSizeBytes?: number;
  tags?: string[];
  isProfile?: boolean;
}

const CATEGORIES = ['Taken', 'Lab', 'Profile'];

const normalizeTags = (raw: string) =>
  raw
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean);

const mergeTags = (manual: string, defaults?: string[]) => {
  const seen = new Set<string>();
  const merged: string[] = [];
  const addTag = (tag: string) => {
    const key = tag.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(tag);
    }
  };

  normalizeTags(manual).forEach(addTag);
  (defaults || []).map(tag => tag.trim()).filter(Boolean).forEach(addTag);
  return merged.join(', ');
};

export default function PatientPhotoGallery({ patientId, token, enableCameraCapture = false, defaultUploadTags = [] }: { patientId: number, token: string, enableCameraCapture?: boolean, defaultUploadTags?: string[] }) {
  const [photos, setPhotos] = useState<PatientPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadCategory, setUploadCategory] = useState('Taken');
  const [uploadTags, setUploadTags] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterTags, setFilterTags] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const buildPhotosUrl = () => {
    const base = getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_PHOTOS, { patientId });
    const params = new URLSearchParams();
    if (filterCategory) params.set('category', filterCategory);
    if (filterTags.trim()) params.set('tags', filterTags.trim());
    if (filterSearch.trim()) params.set('search', filterSearch.trim());
    const query = params.toString();
    return query ? `${base}?${query}` : base;
  };

  const getPhotoUrl = (filePath: string) => {
    const normalizedPath = filePath.replace(/\\/g, '/').replace(/^\/+/, '');
    const base = API_CONFIG.BASE_URL.replace(/\/+$/, '');
    return `${base}/${normalizedPath}`;
  };

  const fetchPhotos = () => {
    setLoading(true);
    fetch(buildPhotosUrl(), { headers: getAuthHeaders(token) })
      .then(res => { if (!res.ok) throw new Error('Failed to fetch photos'); return res.json(); })
      .then(setPhotos)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  // Refresh when patient/token changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPhotos(); }, [patientId, token, filterCategory, filterTags, filterSearch]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraOpen(false);
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    if (defaultUploadTags.length > 0) {
      setUploadTags(prev => prev.trim().length > 0 ? prev : defaultUploadTags.join(', '));
    }
  }, [defaultUploadTags, patientId]);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fileInputRef.current?.files?.length) return;
    const file = fileInputRef.current.files[0];
    const formData = new FormData();
    formData.append('file', file);
    const effectiveTags = mergeTags(uploadTags, defaultUploadTags);
    if (effectiveTags) {
      formData.append('tags', effectiveTags);
    }
    setError('');
    try {
      const uploadUrl = `${getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_PHOTOS, { patientId })}?category=${encodeURIComponent(uploadCategory)}`;
      const res = await fetch(uploadUrl, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (!res.ok) throw new Error('Failed to upload photo');
      fetchPhotos();
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadTags('');
    } catch (err: any) { setError(err.message); }
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Camera is not supported in this browser');
      return;
    }

    setError('');
    setCameraLoading(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      streamRef.current = stream;
      setCameraOpen(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err: any) {
      setError(err?.message || 'Could not open camera');
      stopCamera();
    } finally {
      setCameraLoading(false);
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) {
      setError('Camera is still initializing. Try again in a second.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Could not capture image from camera');
      return;
    }

    ctx.drawImage(video, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) {
      setError('Could not create image file from camera capture');
      return;
    }

    const formData = new FormData();
    const filename = `camera-${new Date().toISOString().replace(/[:.]/g, '-')}.jpg`;
    formData.append('file', new File([blob], filename, { type: 'image/jpeg' }));
    const effectiveTags = mergeTags(uploadTags, defaultUploadTags);
    if (effectiveTags) {
      formData.append('tags', effectiveTags);
    }

    try {
      const uploadUrl = `${getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_PHOTOS, { patientId })}?category=${encodeURIComponent(uploadCategory)}`;
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error('Failed to upload captured photo');
      setUploadTags('');
      fetchPhotos();
    } catch (err: any) {
      setError(err?.message || 'Failed to upload captured photo');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this photo?')) return; setError('');
    try {
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_PHOTO_DELETE, { id }), { method: 'DELETE', headers: getAuthHeaders(token) });
      if (!res.ok) throw new Error('Failed to delete photo');
      fetchPhotos();
    } catch (err: any) { setError(err.message); }
  };

  return (
    <div style={{marginTop:24}}>
      <h3>Photo Gallery</h3>
      <form onSubmit={handleUpload} style={{marginBottom:16}}>
        <input type="file" ref={fileInputRef} accept="image/*" style={{ color: '#f1f5f9' }} />
        <select value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)} style={{ marginLeft: 8, padding: '6px 8px' }}>
          {CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
        </select>
        <input
          type="text"
          value={uploadTags}
          onChange={(e) => setUploadTags(e.target.value)}
          placeholder="tags: xray, urgent"
          style={{ marginLeft: 8, padding: '6px 8px', minWidth: 180 }}
        />
        <button type="submit" style={{ marginLeft: 8, padding: '6px 12px', background: '#1976d2', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Upload Photo</button>
        {enableCameraCapture && (
          <button
            type="button"
            onClick={() => {
              if (cameraOpen) {
                stopCamera();
              } else {
                startCamera();
              }
            }}
            disabled={cameraLoading}
            style={{ marginLeft: 8, padding: '6px 12px', background: '#0ea5e9', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >
            {cameraOpen ? 'Stop Camera' : (cameraLoading ? 'Opening camera...' : 'Use Camera')}
          </button>
        )}
      </form>
      {enableCameraCapture && cameraOpen && (
        <div style={{ marginBottom: 16, background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12 }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Camera capture</div>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', maxWidth: 420, borderRadius: 8, border: '1px solid #334155', background: '#000' }}
          />
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              onClick={capturePhoto}
              style={{ padding: '6px 12px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
            >
              Capture & Upload
            </button>
          </div>
        </div>
      )}
      <div className="responsive-row" style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:16}}>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} style={{ padding: '6px 8px' }}>
          <option value="">All categories</option>
          {CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
        </select>
        <input
          type="text"
          value={filterTags}
          onChange={(e) => setFilterTags(e.target.value)}
          placeholder="filter by tags"
          style={{ padding: '6px 8px', minWidth: 180 }}
        />
        <input
          type="text"
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          placeholder="search filename"
          style={{ padding: '6px 8px', minWidth: 180 }}
        />
      </div>
      {error && <div style={{color:'red'}}>{error}</div>}
      {loading ? <div>Loading...</div> : (
        <div className="responsive-row" style={{display:'flex',flexWrap:'wrap',gap:16}}>
          {photos.length === 0 ? <div>No photos found.</div> : photos.map(photo => (
            <div key={photo.id} style={{border:'1px solid #334155',borderRadius:8,padding:8,background:'#1e293b',position:'relative',minWidth:140, color: '#f1f5f9'}}>
              {photo.isProfile && <div style={{position:'absolute',top:4,left:4,fontSize:10,background:'#1976d2',color:'white',padding:'2px 4px',borderRadius:4}}>Profile</div>}
              {photo.category && <div style={{position:'absolute',top:4,right:4,fontSize:10,background:'#555',color:'white',padding:'2px 4px',borderRadius:4}}>{photo.category}</div>}
              <img src={getPhotoUrl(photo.filePath)} alt={photo.fileName} style={{width:120,height:120,objectFit:'cover',borderRadius:8}} />
              <div style={{fontSize:12,marginTop:4,wordBreak:'break-all'}}>{photo.originalFileName || photo.fileName}</div>
              <div style={{fontSize:12,marginTop:4}}>{formatDate(photo.dateTaken)}</div>
              {photo.tags && photo.tags.length > 0 && (
                <div className="responsive-row" style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:6}}>
                  {photo.tags.map((tag) => (
                    <span key={`${photo.id}-${tag}`} style={{fontSize:11,background:'#334155',padding:'2px 6px',borderRadius:999}}>{tag}</span>
                  ))}
                </div>
              )}
              <button style={{marginTop:8, padding: '4px 8px', background: '#ef4444', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', width: '100%'}} onClick={() => handleDelete(photo.id)}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
